// Smart Sash Admin Panel - Certification Page
// Certification question management with CRUD operations

import {
  ActionButtons,
  QuestionEditor,
  QuestionNavigation,
  SettingsBar,
  InstitutionSelector,
} from '@/components/certification';
import { Alert, Card, PermissionGate, Spinner } from '@/components/common';
import { CertificationConfiguration, CertificationQuestionParsed } from '@/models';
import { useCertificationStore, useInstitutionStore } from '@/store';
import {
  batchQuestionsToParsed,
  cloneQuestions,
  createEmptyQuestion,
  getValidationSummary,
  parsedToQuestion,
  validateQuestion,
} from '@/utils/certification';
import React, { useCallback, useEffect, useState } from 'react';

export const Certification: React.FC = () => {
  // --------------------------------------------------------------------------
  // Store Integration
  // --------------------------------------------------------------------------
  const {
    questions: storeQuestions,
    isLoading,
    error,
    fetchQuestions,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    clearError,
  } = useCertificationStore();

  const { institutions, fetchInstitutions, isLoading: institutionsLoading } = useInstitutionStore();

  // --------------------------------------------------------------------------
  // Local State
  // --------------------------------------------------------------------------
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<CertificationQuestionParsed[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [originalQuestions, setOriginalQuestions] = useState<CertificationQuestionParsed[]>([]);
  const [deletedQuestionIds, setDeletedQuestionIds] = useState<string[]>([]);

  // Settings state
  const [settings, setSettings] = useState<CertificationConfiguration>({
    passingScore: 70,
    shuffleQuestions: false,
    showCorrectAnswers: false,
    allowRetake: true,
  });
  const [allQuestionsEnabled, setAllQuestionsEnabled] = useState<boolean>(true);
  const [numberOfQuestions, setNumberOfQuestions] = useState<number>(10);
  const [randomizeAnswers, setRandomizeAnswers] = useState<boolean>(false);

  // --------------------------------------------------------------------------
  // Lifecycle Effects
  // --------------------------------------------------------------------------

  // Load institutions on mount
  useEffect(() => {
    fetchInstitutions(1, 100);
  }, [fetchInstitutions]);

  // Transform store questions to UI format when store questions change
  useEffect(() => {
    if (storeQuestions.length > 0) {
      const result = batchQuestionsToParsed(storeQuestions);
      if (result.success && result.data) {
        setQuestions(result.data);
        setCurrentQuestionIndex(0);
      }
    } else {
      setQuestions([]);
      setCurrentQuestionIndex(0);
    }
  }, [storeQuestions]);

  // --------------------------------------------------------------------------
  // Event Handlers
  // --------------------------------------------------------------------------

  // Handle institution selection change
  const handleInstitutionChange = useCallback(
    (institutionId: string | null) => {
      setSelectedInstitutionId(institutionId);
      setCurrentQuestionIndex(0);
      clearError();

      // Fetch questions for selected institution
      if (institutionId) {
        fetchQuestions(institutionId);
      } else {
        setQuestions([]);
      }
    },
    [clearError, fetchQuestions]
  );

  // Navigate to previous question
  const handlePreviousQuestion = useCallback(() => {
    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  }, []);

  // Navigate to next question
  const handleNextQuestion = useCallback(() => {
    setCurrentQuestionIndex((prev) => Math.min(questions.length - 1, prev + 1));
  }, [questions.length]);

  // Enter edit mode - backup current questions
  const handleEditClick = useCallback(() => {
    setOriginalQuestions(cloneQuestions(questions));
    setDeletedQuestionIds([]);
    setIsEditMode(true);
    setIsDirty(false);
  }, [questions]);

  // Cancel edit mode - restore backup
  const handleCancelClick = useCallback(() => {
    setQuestions(cloneQuestions(originalQuestions));
    setDeletedQuestionIds([]);
    setIsEditMode(false);
    setIsDirty(false);
    // Reset index if it's now out of bounds
    if (currentQuestionIndex >= originalQuestions.length) {
      setCurrentQuestionIndex(Math.max(0, originalQuestions.length - 1));
    }
  }, [originalQuestions, currentQuestionIndex]);

  // Save changes to API
  const handleSaveClick = useCallback(async () => {
    if (!selectedInstitutionId || !isDirty) return;

    // Validate all questions before saving
    const invalidQuestions = questions
      .map((q, idx) => ({ question: q, index: idx, validation: validateQuestion(q) }))
      .filter(({ validation }) => !validation.valid);

    if (invalidQuestions.length > 0) {
      const summary = getValidationSummary(invalidQuestions[0].validation);
      alert(`Question #${invalidQuestions[0].index + 1} has validation errors:\n${summary}`);
      setCurrentQuestionIndex(invalidQuestions[0].index);
      return;
    }

    setIsSaving(true);

    try {
      // Delete removed questions
      for (const id of deletedQuestionIds) {
        await deleteQuestion(id);
      }

      // Create or update questions
      for (const question of questions) {
        const apiQuestion = parsedToQuestion(question);
        const isNew = question.id.startsWith('new-');

        if (isNew) {
          await createQuestion({
            institutionId: selectedInstitutionId,
            question: apiQuestion.question || '',
            questionType: apiQuestion.questionType || 'MultipleChoice',
            answersJson: apiQuestion.answersJson || '[]',
            correctAnswer: apiQuestion.correctAnswer || '',
            points: apiQuestion.points,
            isActive: apiQuestion.isActive,
          });
        } else {
          await updateQuestion(question.id, {
            question: apiQuestion.question || undefined,
            questionType: apiQuestion.questionType || undefined,
            answersJson: apiQuestion.answersJson || undefined,
            correctAnswer: apiQuestion.correctAnswer || undefined,
            points: apiQuestion.points,
            isActive: apiQuestion.isActive,
          });
        }
      }

      // Refresh questions from server
      await fetchQuestions(selectedInstitutionId);
      setIsEditMode(false);
      setIsDirty(false);
      setDeletedQuestionIds([]);
    } catch {
      // Error is already set in store
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedInstitutionId,
    isDirty,
    questions,
    deletedQuestionIds,
    deleteQuestion,
    createQuestion,
    updateQuestion,
    fetchQuestions,
  ]);

  // Handle question field changes
  const handleQuestionChange = useCallback(
    (updates: Partial<CertificationQuestionParsed>) => {
      setQuestions((prev) => {
        const newQuestions = [...prev];
        newQuestions[currentQuestionIndex] = {
          ...newQuestions[currentQuestionIndex],
          ...updates,
        };
        return newQuestions;
      });
      setIsDirty(true);
    },
    [currentQuestionIndex]
  );

  // Delete current question
  const handleDeleteQuestion = useCallback(() => {
    if (questions.length <= 1) {
      alert('Cannot delete the last question. Add a new question first.');
      return;
    }

    const questionToDelete = questions[currentQuestionIndex];

    // Track deletion if it's an existing question (not a new one)
    if (!questionToDelete.id.startsWith('new-')) {
      setDeletedQuestionIds((prev) => [...prev, questionToDelete.id]);
    }

    setQuestions((prev) => prev.filter((_, idx) => idx !== currentQuestionIndex));
    setCurrentQuestionIndex((prev) => Math.min(prev, questions.length - 2));
    setIsDirty(true);
  }, [questions, currentQuestionIndex]);

  // Add new question
  const handleAddQuestion = useCallback(() => {
    if (!selectedInstitutionId) return;

    const newQuestion = createEmptyQuestion(selectedInstitutionId);
    // Generate a temporary ID for new questions (will be replaced by server on save)
    newQuestion.id = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setQuestions((prev) => [...prev, newQuestion]);
    setCurrentQuestionIndex(questions.length);
    setIsDirty(true);
  }, [selectedInstitutionId, questions.length]);

  // Shift question left (earlier in order)
  const handleShiftLeft = useCallback(() => {
    if (currentQuestionIndex <= 0) return;

    setQuestions((prev) => {
      const newQuestions = [...prev];
      const temp = newQuestions[currentQuestionIndex - 1];
      newQuestions[currentQuestionIndex - 1] = newQuestions[currentQuestionIndex];
      newQuestions[currentQuestionIndex] = temp;
      return newQuestions;
    });
    setCurrentQuestionIndex((prev) => prev - 1);
    setIsDirty(true);
  }, [currentQuestionIndex]);

  // Shift question right (later in order)
  const handleShiftRight = useCallback(() => {
    if (currentQuestionIndex >= questions.length - 1) return;

    setQuestions((prev) => {
      const newQuestions = [...prev];
      const temp = newQuestions[currentQuestionIndex + 1];
      newQuestions[currentQuestionIndex + 1] = newQuestions[currentQuestionIndex];
      newQuestions[currentQuestionIndex] = temp;
      return newQuestions;
    });
    setCurrentQuestionIndex((prev) => prev + 1);
    setIsDirty(true);
  }, [currentQuestionIndex, questions.length]);

  // Handle settings change
  const handleSettingsChange = useCallback((updates: Partial<CertificationConfiguration>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
  }, []);

  // Handle UI-only settings change
  const handleUISettingsChange = useCallback((field: string, value: boolean | number) => {
    switch (field) {
      case 'allQuestionsEnabled':
        setAllQuestionsEnabled(value as boolean);
        break;
      case 'numberOfQuestions':
        setNumberOfQuestions(value as number);
        break;
      case 'randomizeAnswers':
        setRandomizeAnswers(value as boolean);
        break;
    }
    setIsDirty(true);
  }, []);

  // --------------------------------------------------------------------------
  // Derived State
  // --------------------------------------------------------------------------
  const currentQuestion = questions[currentQuestionIndex] || null;
  const isLoadingAny = isLoading || institutionsLoading;

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div
      className={`container-fluid py-4 certification-page ${isEditMode ? 'certification-edit-mode' : 'certification-view-mode'}`}
      role="main"
      aria-label="Certification Management"
    >
      {/* Header Row */}
      <header className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-0">Certification Questions</h1>
              <p className="text-muted">
                {isEditMode
                  ? 'Edit certification questions'
                  : 'View certification questions for each institution'}
              </p>
            </div>
            {/* Action Buttons */}
            {selectedInstitutionId && (
              <PermissionGate action="certification:edit">
                <ActionButtons
                  isEditMode={isEditMode}
                  isDirty={isDirty}
                  isSaving={isSaving}
                  disabled={!selectedInstitutionId || isLoadingAny}
                  onEditClick={handleEditClick}
                  onCancelClick={handleCancelClick}
                  onSaveClick={handleSaveClick}
                />
              </PermissionGate>
            )}
          </div>
        </div>
      </header>

      {/* Alert Row: Error Messages */}
      <div role="alert" aria-live="assertive" aria-atomic="true">
        {error && (
          <div className="row mb-3">
            <div className="col-12">
              <Alert variant="danger" dismissible onDismiss={clearError}>
                {error}
              </Alert>
            </div>
          </div>
        )}
      </div>

      {/* Institution Selector Row */}
      <div className="row mb-4">
        <InstitutionSelector
          institutions={institutions}
          selectedInstitutionId={selectedInstitutionId}
          onInstitutionChange={handleInstitutionChange}
          disabled={isEditMode}
          isLoading={institutionsLoading}
        />
      </div>

      {/* Settings Bar Row */}
      {selectedInstitutionId && questions.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <Card className="border-0 shadow-sm">
              <SettingsBar
                isEditMode={isEditMode}
                settings={settings}
                allQuestionsEnabled={allQuestionsEnabled}
                numberOfQuestions={numberOfQuestions}
                randomizeAnswers={randomizeAnswers}
                onSettingsChange={handleSettingsChange}
                onUISettingsChange={handleUISettingsChange}
              />
            </Card>
          </div>
        </div>
      )}

      {/* Main Content */}
      <section className="row" aria-label="Certification content">
        <div className="col-12">
          {/* Loading State */}
          {isLoadingAny && (
            <Card className="border-0 shadow-sm">
              <div
                className="text-center py-5"
                role="status"
                aria-busy="true"
                aria-label="Loading content"
              >
                <Spinner size="lg" />
                <p className="text-muted mt-3">Loading...</p>
              </div>
            </Card>
          )}

          {/* Empty State - No Institution Selected */}
          {!isLoadingAny && !selectedInstitutionId && (
            <Card className="border-0 shadow-sm">
              <div className="text-center py-5">
                <i className="fas fa-building fa-3x text-muted mb-3"></i>
                <h4>Select an Institution</h4>
                <p className="text-muted">
                  Choose an institution from the dropdown above to view certification questions.
                </p>
              </div>
            </Card>
          )}

          {/* Empty State - No Questions */}
          {!isLoadingAny && selectedInstitutionId && questions.length === 0 && (
            <Card className="border-0 shadow-sm">
              <div className="text-center py-5">
                <i className="fas fa-question-circle fa-3x text-muted mb-3"></i>
                <h4>No Questions Found</h4>
                <p className="text-muted">This institution has no certification questions.</p>
                {isEditMode && (
                  <button className="btn btn-primary mt-3" onClick={handleAddQuestion}>
                    <i className="fas fa-plus me-2"></i>
                    Add First Question
                  </button>
                )}
              </div>
            </Card>
          )}

          {/* Question Display Interface */}
          {!isLoadingAny && selectedInstitutionId && questions.length > 0 && (
            <>
              {/* Question Editor */}
              <Card className="border-0 shadow-sm mb-4">
                <QuestionEditor
                  question={currentQuestion}
                  questionNumber={currentQuestionIndex + 1}
                  totalQuestions={questions.length}
                  isEditMode={isEditMode}
                  onQuestionChange={handleQuestionChange}
                  onDelete={handleDeleteQuestion}
                />
              </Card>

              {/* Question Navigation */}
              <Card className="border-0 shadow-sm">
                <QuestionNavigation
                  currentIndex={currentQuestionIndex}
                  totalQuestions={questions.length}
                  isEditMode={isEditMode}
                  onPrevious={handlePreviousQuestion}
                  onNext={handleNextQuestion}
                  onShiftLeft={handleShiftLeft}
                  onShiftRight={handleShiftRight}
                  onAdd={handleAddQuestion}
                />
              </Card>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Certification;
